import React, { useState } from 'react';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import {
  Cloud, Shield, ArrowRight, Check, Copy, AlertTriangle,
  DollarSign, TrendingDown, Zap, Lock, Eye, EyeOff, Server,
  RefreshCw, CheckCircle2, XCircle, Clock, ExternalLink, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { CLOUD_PROVIDERS } from '../constants/cloudProviders';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUDIT_STEPS = {
  aws: [
    { step: 1, title: 'Create IAM Role', instruction: 'Go to AWS Console → IAM → Roles → Create Role', detail: 'Select "Another AWS account" and enter our Account ID. This grants read-only access — we CANNOT modify your resources.', code: '{\n  "Version": "2012-10-17",\n  "Statement": [{\n    "Effect": "Allow",\n    "Principal": {"AWS": "arn:aws:iam::123456789012:root"},\n    "Action": "sts:AssumeRole",\n    "Condition": {"StringEquals": {"sts:ExternalId": "cartie-audit-UNIQUE_ID"}}\n  }]\n}' },
    { step: 2, title: 'Attach Read-Only Policy', instruction: 'Attach the AWS managed policy: ReadOnlyAccess', detail: 'This policy allows ONLY reading data — no create, update, or delete permissions.', code: 'arn:aws:iam::aws:policy/ReadOnlyAccess' },
    { step: 3, title: 'Copy Role ARN', instruction: 'Copy the Role ARN after creation', detail: 'It looks like: arn:aws:iam::YOUR_ACCOUNT:role/CartieAuditRole', code: 'arn:aws:iam::YOUR_ACCOUNT_ID:role/CartieAuditRole' },
  ],
  azure: [
    { step: 1, title: 'Register App in Azure AD', instruction: 'Azure Portal → Azure AD → App Registrations → New', detail: 'Create a new app registration named "CARTIEAI Audit". This creates a service principal with read access only.', code: 'App Name: CARTIEAI Audit\nSupported account type: Single tenant' },
    { step: 2, title: 'Assign Reader Role', instruction: 'Go to Subscription → Access Control (IAM) → Add Role Assignment', detail: 'Assign the "Cost Management Reader" role to the app.', code: 'Role: Cost Management Reader\nScope: Subscription level' },
    { step: 3, title: 'Copy Credentials', instruction: 'Copy Tenant ID, Client ID, and create a Client Secret', detail: 'Go to App Registrations → Your App → Certificates & Secrets → New Client Secret', code: 'Tenant ID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx\nClient ID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
  ],
  gcp: [
    { step: 1, title: 'Create Service Account', instruction: 'GCP Console → IAM → Service Accounts → Create', detail: 'Name it "cartie-audit". This creates a limited service account for read-only billing data.', code: 'Service Account ID: cartie-audit@YOUR_PROJECT.iam.gserviceaccount.com' },
    { step: 2, title: 'Grant Billing Viewer Role', instruction: 'IAM → Add → Billing Account Viewer role', detail: 'This role allows reading billing/cost data only.', code: 'Role: roles/billing.viewer\nScope: Billing Account' },
    { step: 3, title: 'Download JSON Key', instruction: 'Service Accounts → Keys → Add Key → JSON', detail: 'Download the JSON key file and upload it here.', code: '{\n  "type": "service_account",\n  "project_id": "your-project"\n}' },
  ],
  snowflake: [
    { step: 1, title: 'Create Read-Only User', instruction: 'Snowflake Console → Admin → Users → Create', detail: 'Create a user named "cartie_audit" with read-only warehouse access.', code: 'CREATE USER cartie_audit\n  PASSWORD = \'...\'\n  DEFAULT_ROLE = cartie_reader;' },
    { step: 2, title: 'Create Reader Role', instruction: 'Grant USAGE on warehouses and MONITOR privileges', detail: 'This role can only read usage data and query history — no data access.', code: 'CREATE ROLE cartie_reader;\nGRANT MONITOR ON ACCOUNT TO ROLE cartie_reader;\nGRANT USAGE ON WAREHOUSE compute_wh TO ROLE cartie_reader;' },
    { step: 3, title: 'Copy Connection Details', instruction: 'Copy your Snowflake account URL and credentials', detail: 'Account URL format: your_account.snowflakecomputing.com', code: 'Account: your_account.snowflakecomputing.com\nUser: cartie_audit\nRole: cartie_reader' },
  ],
  oracle: [
    { step: 1, title: 'Create IAM Policy', instruction: 'OCI Console → Identity → Policies → Create', detail: 'Create a policy granting inspect and read access to cost data.', code: 'Allow group CartieAuditGroup to inspect all-resources in tenancy\nAllow group CartieAuditGroup to read usage-reports in tenancy' },
    { step: 2, title: 'Create API Key', instruction: 'User Settings → API Keys → Add API Key', detail: 'Generate an API signing key pair for authentication.', code: 'Tenancy OCID: ocid1.tenancy.oc1..xxxxx\nUser OCID: ocid1.user.oc1..xxxxx' },
    { step: 3, title: 'Copy Configuration', instruction: 'Copy your tenancy OCID, user OCID, and fingerprint', detail: 'These are found in your OCI config file after key creation.', code: '[DEFAULT]\ntenancy=ocid1.tenancy.oc1..xxxxx\nuser=ocid1.user.oc1..xxxxx\nfingerprint=xx:xx:xx' },
  ],
  ibm: [
    { step: 1, title: 'Create Service ID', instruction: 'IBM Cloud → Manage → Access (IAM) → Service IDs → Create', detail: 'Create a Service ID named "cartie-audit" for read-only billing access.', code: 'Service ID: cartie-audit\nDescription: CARTIEAI read-only billing audit' },
    { step: 2, title: 'Assign Viewer Role', instruction: 'Assign the "Viewer" and "Usage Report Viewer" roles', detail: 'These roles allow reading billing and usage data only.', code: 'Roles: Viewer, Usage Report Viewer\nScope: Account level' },
    { step: 3, title: 'Create API Key', instruction: 'Service IDs → Your Service ID → API Keys → Create', detail: 'Copy the API key — this is used for authentication.', code: 'API Key: xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
  ],
  alibaba: [
    { step: 1, title: 'Create RAM User', instruction: 'Alibaba Cloud Console → RAM → Users → Create User', detail: 'Create a user with programmatic access for billing read-only.', code: 'User: cartie-audit\nAccess Mode: Programmatic Access' },
    { step: 2, title: 'Attach BillingReadOnly Policy', instruction: 'Grant AliyunBSSReadOnlyAccess policy', detail: 'This policy allows reading billing data only — no resource access.', code: 'Policy: AliyunBSSReadOnlyAccess\nScope: Account level' },
    { step: 3, title: 'Copy Access Keys', instruction: 'Copy the AccessKey ID and AccessKey Secret', detail: 'These credentials are shown only once during creation.', code: 'AccessKey ID: LTAIxxxxxxxxxx\nAccessKey Secret: ****' },
  ],
  databricks: [
    { step: 1, title: 'Create Service Principal', instruction: 'Databricks → Admin Console → Service Principals → Add', detail: 'Create a service principal for read-only access to usage data.', code: 'Name: cartie-audit\nEntitlement: Workspace access' },
    { step: 2, title: 'Grant Account Admin Read', instruction: 'Assign "Account Usage Reader" permission', detail: 'This allows reading system tables for cost and usage analysis.', code: 'GRANT SELECT ON SYSTEM.BILLING TO `cartie-audit`;\nGRANT SELECT ON SYSTEM.USAGE TO `cartie-audit`;' },
    { step: 3, title: 'Generate Token', instruction: 'Create a personal access token or OAuth token', detail: 'Go to User Settings → Developer → Access Tokens → Generate.', code: 'Workspace URL: https://your-workspace.cloud.databricks.com\nToken: dapi_xxxxxxxxxxxxxx' },
  ],
  digitalocean: [
    { step: 1, title: 'Create API Token', instruction: 'DigitalOcean → API → Tokens → Generate New Token', detail: 'Create a read-only token for billing and resource data.', code: 'Token Name: cartie-audit\nScope: Read Only' },
    { step: 2, title: 'Copy Token', instruction: 'Copy the token immediately — it won\'t be shown again', detail: 'This token provides read-only access to your account billing data.', code: 'Token: dop_v1_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
  ],
  cloudflare: [
    { step: 1, title: 'Create API Token', instruction: 'Cloudflare → My Profile → API Tokens → Create Token', detail: 'Use the "Read all resources" template for read-only access.', code: 'Template: Read all resources\nZone: All zones' },
    { step: 2, title: 'Copy Token', instruction: 'Copy the API token', detail: 'This provides read-only access to your Cloudflare account billing.', code: 'API Token: xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx\nAccount ID: your_account_id' },
  ],
  linode: [
    { step: 1, title: 'Create Personal Access Token', instruction: 'Linode → My Profile → API Tokens → Create', detail: 'Create a token with read-only access to account and billing.', code: 'Label: cartie-audit\nExpiry: 6 months\nAccess: Read Only (all)' },
    { step: 2, title: 'Copy Token', instruction: 'Copy the token immediately', detail: 'This token provides read-only access to your Linode billing data.', code: 'Token: xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
  ],
  hetzner: [
    { step: 1, title: 'Create API Token', instruction: 'Hetzner Cloud Console → Security → API Tokens → Generate', detail: 'Create a read-only API token for your project.', code: 'Description: cartie-audit\nPermission: Read Only' },
    { step: 2, title: 'Copy Token', instruction: 'Copy the token', detail: 'This provides read-only access to your Hetzner project resources.', code: 'API Token: xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
  ],
  vultr: [
    { step: 1, title: 'Create API Key', instruction: 'Vultr → Account → API → Enable API and copy key', detail: 'The API key provides access to billing and resource data.', code: 'API Key: xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
    { step: 2, title: 'Restrict Access', instruction: 'Whitelist CARTIEAI IP addresses for security', detail: 'Add our IP range to the API access control list.', code: 'Allowed IPs: 52.xx.xx.xx/32 (provided after signup)' },
  ],
  ovh: [
    { step: 1, title: 'Create Application', instruction: 'OVH API → Create Application at api.ovh.com/createApp', detail: 'Register a new application with read-only permissions.', code: 'App Name: CARTIEAI Audit\nDescription: Read-only billing access' },
    { step: 2, title: 'Generate Consumer Key', instruction: 'Request a consumer key with GET /me/bill permissions', detail: 'This key only allows reading billing data.', code: 'Application Key: xxxxxxxx\nApplication Secret: xxxxxxxx\nConsumer Key: xxxxxxxx' },
  ],
  wasabi: [
    { step: 1, title: 'Create Sub-User', instruction: 'Wasabi Console → Users → Create User', detail: 'Create a sub-user with read-only access to billing.', code: 'User: cartie-audit\nAccess Type: Programmatic' },
    { step: 2, title: 'Copy Access Keys', instruction: 'Copy the Access Key and Secret Key', detail: 'These credentials allow reading your storage usage and billing data.', code: 'Access Key: xxxxxxxxxxxxxxxx\nSecret Key: xxxxxxxxxxxxxxxx' },
  ],
};

const DEMO_AUDIT_RESULTS = {
  provider: 'AWS',
  accountId: '****-7842',
  scannedAt: new Date().toISOString(),
  resourcesScanned: 847,
  totalMonthlySpend: 67420,
  wasteFound: 18740,
  wastePercent: 27.8,
  savingsBreakdown: [
    { category: 'Idle/Zombie Resources', count: 23, monthlySavings: 6840, items: ['12 unattached EBS volumes ($2,400)', '4 idle EC2 instances ($3,100)', '7 unused Elastic IPs ($340)'] },
    { category: 'Right-Sizing Opportunities', count: 31, monthlySavings: 5200, items: ['18 over-provisioned EC2 (avg 12% CPU utilization)', '8 oversized RDS instances', '5 over-allocated Lambda memory'] },
    { category: 'Reserved Instance Gaps', count: 8, monthlySavings: 4200, items: ['On-demand instances that should be Reserved ($3,400)', 'Expiring RIs not renewed ($800)'] },
    { category: 'Storage Optimization', count: 14, monthlySavings: 1800, items: ['8.4TB in S3 Standard that should be Glacier ($1,200)', 'Duplicate snapshots older than 90 days ($600)'] },
    { category: 'Data Transfer', count: 4, monthlySavings: 700, items: ['Cross-region replication on non-critical buckets ($500)', 'NAT Gateway idle hours ($200)'] },
  ],
  quickWins: [
    { action: 'Delete 12 unattached EBS volumes', savings: 2400, effort: '5 min', risk: 'None' },
    { action: 'Stop 4 idle EC2 instances (0% CPU for 14+ days)', savings: 3100, effort: '2 min', risk: 'Low' },
    { action: 'Right-size 18 EC2 instances to t3.medium', savings: 2800, effort: '30 min', risk: 'Low' },
  ],
};

const CloudAudit = () => {
  const { shouldShowData } = useDemoData();
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [auditMethod, setAuditMethod] = useState(null); // 'csv' or 'keys'
  const [currentStep, setCurrentStep] = useState(0);
  const [credentials, setCredentials] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [tenantId, setTenantId] = useState(''); // Azure-only
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [auditResults, setAuditResults] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  // Normalize backend snake_case response → frontend camelCase
  const normalizeAuditResult = (r) => ({
    provider: r.provider || 'Cloud',
    accountId: r.account_id || r.accountId || '',
    scannedAt: r.scanned_at || r.scannedAt,
    resourcesScanned: r.resources_scanned ?? r.resourcesScanned ?? 0,
    totalMonthlySpend: r.total_monthly_spend ?? r.totalMonthlySpend ?? 0,
    wasteFound: r.waste_found ?? r.wasteFound ?? 0,
    wastePercent: r.waste_percent ?? r.wastePercent ?? 0,
    savingsBreakdown: (r.savings_breakdown || r.savingsBreakdown || []).map(s => ({
      category: s.category,
      count: s.count,
      monthlySavings: s.monthly_savings ?? s.monthlySavings ?? 0,
      items: s.items || [],
    })),
    quickWins: (r.quick_wins || r.quickWins || []).map(q => ({
      action: q.action,
      savings: q.savings || 0,
      effort: q.effort,
      risk: q.risk,
    })),
    note: r.note,
  });

  const allProviders = CLOUD_PROVIDERS.filter(p => AUDIT_STEPS[p.id]);
  // 6 LIVE providers shown as primary tiles. The 9 'SOON' providers are tucked into "show more".
  const LIVE_PROVIDER_IDS = ['aws', 'azure', 'gcp', 'snowflake', 'databricks', 'digitalocean'];
  const majorProviders = allProviders.filter(p => LIVE_PROVIDER_IDS.includes(p.id));
  const otherProviders = allProviders.filter(p => !LIVE_PROVIDER_IDS.includes(p.id));
  const [showAllProviders, setShowAllProviders] = useState(false);

  const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(e.type === 'dragenter' || e.type === 'dragover'); };
  const handleDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.json'))) {
      setCsvFile(file); toast.success(`${file.name} uploaded!`);
    } else { toast.error('Please upload a CSV or JSON file'); }
  };
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) { setCsvFile(file); toast.success(`${file.name} uploaded!`); }
  };

  const handleCsvAudit = async () => {
    setScanning(true); setScanProgress(0);
    
    if (csvFile && !shouldShowData) {
      try {
        const formData = new FormData();
        formData.append('file', csvFile);
        formData.append('provider', selectedProvider?.id || 'aws');
        const token = localStorage.getItem('token');
        setScanProgress(30);
        const response = await axios.post(`${API_URL}/api/cloud-audit/csv`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        setScanProgress(100); setScanning(false);
        setAuditResults(normalizeAuditResult(response.data));
        toast.success(`Bill analysis complete! Found $${(response.data.waste_found || 0).toLocaleString()}/mo in potential savings.`);
        return;
      } catch (err) {
        setScanning(false); setScanProgress(0);
        toast.error(err.response?.data?.detail || 'Failed to parse bill. Check file format.');
        return;
      }
    }

    // Demo mode
    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval); setScanning(false);
          setAuditResults({...DEMO_AUDIT_RESULTS, scan_type: 'csv'});
          toast.success('Bill analysis complete! Found $18,740/mo in potential savings.');
          return 100;
        }
        return prev + 5;
      });
    }, 100);
  };

  const handleStartScan = async () => {
    setScanning(true);
    setScanProgress(0);
    
    // If real credentials provided, call real API
    if (credentials && !shouldShowData) {
      try {
        setScanProgress(20);
        const token = localStorage.getItem('token');
        let payload, endpoint;
        
        if (selectedProvider?.id === 'aws') {
          payload = secretKey 
            ? { access_key_id: credentials, secret_access_key: secretKey, region: 'us-east-1' }
            : { role_arn: credentials, region: 'us-east-1' };
          endpoint = '/api/cloud-audit/aws';
        } else if (selectedProvider?.id === 'azure') {
          if (!tenantId) {
            toast.error('Tenant ID is required for Azure.');
            setScanning(false); return;
          }
          payload = { client_id: credentials, client_secret: secretKey, tenant_id: tenantId };
          endpoint = '/api/cloud-audit/azure';
        } else if (selectedProvider?.id === 'gcp') {
          payload = { service_account_json: credentials };
          endpoint = '/api/cloud-audit/gcp';
        } else if (selectedProvider?.id === 'snowflake') {
          if (!tenantId) {
            toast.error('Account identifier is required for Snowflake.');
            setScanning(false); return;
          }
          payload = { account: tenantId, username: credentials, password: secretKey };
          endpoint = '/api/cloud-audit/snowflake';
        } else if (selectedProvider?.id === 'databricks') {
          if (!tenantId) {
            toast.error('Workspace URL is required for Databricks.');
            setScanning(false); return;
          }
          payload = { workspace_url: tenantId, access_token: credentials };
          endpoint = '/api/cloud-audit/databricks';
        } else if (selectedProvider?.id === 'digitalocean') {
          payload = { access_token: credentials };
          endpoint = '/api/cloud-audit/digitalocean';
        } else {
          // Other providers - use CSV for now
          toast.info(`For ${selectedProvider?.name}, please use the CSV upload option for now.`);
          setScanning(false); return;
        }
        
        const response = await axios.post(`${API_URL}${endpoint}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setScanProgress(100);
        setScanning(false);
        setAuditResults(normalizeAuditResult(response.data));
        toast.success(`Audit complete! Found $${(response.data.waste_found || 0).toLocaleString()}/mo in savings.`);
        return;
      } catch (err) {
        setScanning(false);
        setScanProgress(0);
        const msg = err.response?.data?.detail || 'Failed to connect. Check your credentials and try again.';
        toast.error(msg);
        return;
      }
    }

    // Demo mode: simulate scan with progress
    const interval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setScanning(false);
          setAuditResults(DEMO_AUDIT_RESULTS);
          toast.success('Demo audit complete! Found $18,740/mo in savings.');
          return 100;
        }
        return prev + 4;
      });
    }, 120);
  };

  const providerSteps = selectedProvider ? AUDIT_STEPS[selectedProvider.id] : [];

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="cloud-audit-page">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2"><BrandTitle>Free Cloud Audit</BrandTitle></h1>
        <p className="text-slate-400 max-w-xl mx-auto">Connect your cloud account with read-only access. Get a complete waste analysis in 5 minutes. No commitment, no credit card.</p>
      </div>

      {!auditResults ? (
        <>
          {/* Step 1: Select Provider */}
          {!selectedProvider && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Cloud className="w-5 h-5 text-emerald-500" />Select Your Cloud Provider</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-3">Live Cloud Providers — Connect Now</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                  {majorProviders.map(provider => (
                    <button key={provider.id} onClick={() => setSelectedProvider(provider)}
                      className="relative p-5 rounded-xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-center group"
                      data-testid={`select-${provider.id}`}>
                      <span className="absolute top-2 right-2 text-[11px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 border border-emerald-500/30">
                        Live
                      </span>
                      <provider.LogoComponent />
                      <p className="mt-2 font-semibold text-sm text-slate-800 group-hover:text-emerald-700">{provider.name}</p>
                      <p className="text-xs text-slate-400 truncate">{provider.fullName}</p>
                    </button>
                  ))}
                </div>
                {!showAllProviders ? (
                  <button onClick={() => setShowAllProviders(true)} className="text-sm text-emerald-600 hover:text-emerald-500 font-medium w-full text-center py-2" data-testid="show-more-providers">
                    + {otherProviders.length} more providers coming soon (Oracle, IBM, Alibaba, Cloudflare, Hetzner...)
                  </button>
                ) : (
                  <>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-3 mt-2">Coming Soon — Request Integration</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                      {otherProviders.map(provider => (
                        <button key={provider.id} onClick={() => setSelectedProvider(provider)}
                          className="relative p-4 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all text-center group opacity-90"
                          data-testid={`select-${provider.id}`}>
                          <span className="absolute top-1.5 right-1.5 text-[8px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 border border-amber-500/30">
                            Soon
                          </span>
                          <provider.LogoComponent />
                          <p className="mt-2 font-semibold text-xs text-slate-800 group-hover:text-amber-700">{provider.name}</p>
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg flex items-center gap-2">
                  <Lock className="w-4 h-4 text-emerald-500 shrink-0" />
                  <p className="text-xs text-emerald-700">Your data stays private. We CANNOT modify, delete, or create any resources.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Choose Method */}
          {selectedProvider && !auditMethod && !scanning && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2"><selectedProvider.LogoComponent />How do you want to connect?</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedProvider(null)}>Change Provider</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Option A: CSV Upload - EASY */}
                  <button onClick={() => setAuditMethod('csv')}
                    className="p-6 rounded-xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left group" data-testid="method-csv">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                        <FileText className="w-5 h-5 text-emerald-600" />
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[11px]">Easiest</Badge>
                    </div>
                    <p className="font-bold text-slate-800 mb-1">Upload Your Bill (CSV)</p>
                    <p className="text-sm text-slate-400 mb-3">Just download your billing CSV and drop it here. Zero credentials needed.</p>
                    <ul className="text-xs text-slate-400 space-y-1">
                      <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />No credentials required</li>
                      <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />Cost breakdown & trends</li>
                      <li className="flex items-center gap-1"><XCircle className="w-3 h-3 text-slate-300" />No resource-level scanning</li>
                    </ul>
                  </button>

                  {/* Option B: Access Keys - DEEPER */}
                  <button onClick={() => setAuditMethod('keys')}
                    className="p-6 rounded-xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left group" data-testid="method-keys">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <Lock className="w-5 h-5 text-emerald-600" />
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[11px]">Deeper Scan</Badge>
                    </div>
                    <p className="font-bold text-slate-800 mb-1">Connect with Access Keys</p>
                    <p className="text-sm text-slate-400 mb-3">Paste your read-only keys for a full resource-level audit.</p>
                    <ul className="text-xs text-slate-400 space-y-1">
                      <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />Cost breakdown & trends</li>
                      <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />Find idle/zombie resources</li>
                      <li className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" />Right-sizing recommendations</li>
                    </ul>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3A: CSV Upload */}
          {selectedProvider && auditMethod === 'csv' && !scanning && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-emerald-500" />Upload Your {selectedProvider.name} Bill</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setAuditMethod(null)}>Back</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-sm text-slate-700 font-medium mb-1">Where to download your bill:</p>
                  <p className="text-xs text-slate-400">
                    {selectedProvider.id === 'aws' ? 'AWS Console → Billing → Bills → Download CSV' :
                     selectedProvider.id === 'azure' ? 'Azure Portal → Cost Management → Export → Download CSV' :
                     selectedProvider.id === 'gcp' ? 'GCP Console → Billing → Reports → Export CSV' :
                     selectedProvider.id === 'snowflake' ? 'Snowflake → Admin → Usage → Download' :
                     `${selectedProvider.name} Dashboard → Billing → Export / Download`}
                  </p>
                </div>

                {/* Drop zone */}
                <div className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${dragActive ? 'border-emerald-500 bg-emerald-50' : csvFile ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-300 hover:border-emerald-400'}`}
                  onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                  onClick={() => document.getElementById('csv-input').click()} data-testid="csv-drop-zone">
                  <input id="csv-input" type="file" accept=".csv,.json" className="hidden" onChange={handleFileSelect} />
                  {csvFile ? (
                    <div className="space-y-2">
                      <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                      <p className="font-semibold text-emerald-700">{csvFile.name}</p>
                      <p className="text-xs text-slate-400">{(csvFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Cloud className="w-10 h-10 text-slate-400 mx-auto" />
                      <p className="font-semibold text-slate-700">Drop your billing CSV here</p>
                      <p className="text-xs text-slate-400">or click to browse. Supports CSV and JSON formats.</p>
                    </div>
                  )}
                </div>

                <Button className="bg-emerald-500 hover:bg-emerald-600 w-full h-12 text-base" onClick={handleCsvAudit}
                  disabled={!shouldShowData && !csvFile} data-testid="analyze-csv-btn">
                  <Zap className="w-5 h-5 mr-2" />
                  {shouldShowData ? 'Run Demo Analysis' : csvFile ? 'Analyze My Bill' : 'Upload a file first'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 3B: Access Keys with Visual Guide */}
          {selectedProvider && auditMethod === 'keys' && !scanning && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-emerald-500" />Connect {selectedProvider.name}</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowGuide(!showGuide)} data-testid="toggle-guide-btn">
                      {showGuide ? <><EyeOff className="w-3.5 h-3.5 mr-1" />Hide Guide</> : <><Eye className="w-3.5 h-3.5 mr-1" />Show Me How</>}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setAuditMethod(null)}>Back</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Interactive Visual Guide */}
                {showGuide && (
                  <div className="space-y-3 mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">1-Minute Guide</Badge>
                      <span className="text-xs text-slate-400">Follow these 4 simple steps</span>
                    </div>

                    {selectedProvider.id === 'aws' && [
                      { num: 1, title: 'Open AWS Console', visual: (
                        <div className="bg-slate-900 rounded-lg p-3">
                          <div className="flex items-center gap-2 mb-2"><div className="flex gap-1"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /><div className="w-2.5 h-2.5 rounded-full bg-yellow-500" /><div className="w-2.5 h-2.5 rounded-full bg-green-500" /></div><span className="text-[11px] text-slate-400">console.aws.amazon.com</span></div>
                          <div className="flex items-center gap-2 bg-slate-800 rounded px-2 py-1"><span className="text-xs text-white">Search:</span><span className="text-xs text-blue-400 font-mono bg-emerald-500/10 px-2 py-0.5 rounded">IAM</span><span className="text-[11px] text-slate-400">← Type this</span></div>
                        </div>
                      )},
                      { num: 2, title: 'Go to Users → Your User → Security Credentials', visual: (
                        <div className="bg-slate-900 rounded-lg p-3">
                          <div className="flex gap-2 text-[11px] mb-2"><span className="text-slate-400">IAM</span><span className="text-slate-400">&gt;</span><span className="text-blue-400">Users</span><span className="text-slate-400">&gt;</span><span className="text-blue-400">your-name</span><span className="text-slate-400">&gt;</span><span className="text-emerald-400 font-bold bg-emerald-500/10 px-1 rounded">Security credentials</span></div>
                          <div className="flex gap-2"><div className="px-2 py-1 bg-slate-800 rounded text-[11px] text-slate-400">Permissions</div><div className="px-2 py-1 bg-slate-800 rounded text-[11px] text-slate-400">Groups</div><div className="px-2 py-1 bg-emerald-500/20 border border-emerald-500/40 rounded text-[11px] text-blue-300">Security credentials</div></div>
                        </div>
                      )},
                      { num: 3, title: 'Click "Create Access Key"', visual: (
                        <div className="bg-slate-900 rounded-lg p-3">
                          <p className="text-[11px] text-slate-400 mb-2">Access keys (0)</p>
                          <div className="inline-flex items-center gap-1 bg-emerald-500 text-white text-xs px-3 py-1.5 rounded cursor-pointer animate-pulse"><span>Create access key</span></div>
                          <p className="text-[11px] text-emerald-400 mt-2">← Click this button</p>
                        </div>
                      )},
                      { num: 4, title: 'Copy both values and paste below', visual: (
                        <div className="bg-slate-900 rounded-lg p-3 space-y-2">
                          <div className="flex items-center justify-between bg-slate-800 rounded px-2 py-1"><span className="text-[11px] text-slate-400">Access key ID:</span><span className="text-[11px] text-emerald-400 font-mono">AKIAIOSFODNN7EXAMPLE</span><span className="text-[11px] bg-slate-700 px-1 rounded text-slate-300">copy</span></div>
                          <div className="flex items-center justify-between bg-slate-800 rounded px-2 py-1"><span className="text-[11px] text-slate-400">Secret access key:</span><span className="text-[11px] text-emerald-400 font-mono">wJalrXUtnFEMI/K7MDENG...</span><span className="text-[11px] bg-slate-700 px-1 rounded text-slate-300">copy</span></div>
                          <p className="text-[11px] text-amber-400">Paste them in the fields below</p>
                        </div>
                      )},
                    ].map((step) => (
                      <div key={step.num} className="flex gap-3 items-start">
                        <div className="w-7 h-7 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-1">{step.num}</div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-800 mb-1.5">{step.title}</p>
                          {step.visual}
                        </div>
                      </div>
                    ))}

                    {selectedProvider.id !== 'aws' && (
                      <div className="p-4 bg-emerald-50 rounded-xl border border-blue-100 space-y-2">
                        {(selectedProvider.id === 'azure' ? [
                          { num: 1, text: 'Azure Portal → Search "App registrations" → New registration' },
                          { num: 2, text: 'Name it "CARTIE Audit" → Register → Copy Application (client) ID' },
                          { num: 3, text: 'Certificates & secrets → New client secret → Copy the secret value' },
                          { num: 4, text: 'Paste Client ID and Secret below' },
                        ] : selectedProvider.id === 'gcp' ? [
                          { num: 1, text: 'GCP Console → IAM & Admin → Service Accounts → Create' },
                          { num: 2, text: 'Name: "cartie-audit" → Grant Billing Viewer role' },
                          { num: 3, text: 'Keys tab → Add Key → JSON → Download file' },
                          { num: 4, text: 'Open JSON file → Copy the content → Paste below' },
                        ] : selectedProvider.id === 'snowflake' ? [
                          { num: 1, text: 'Snowflake UI → Admin → Users & Roles → Create a new read-only user' },
                          { num: 2, text: 'Assign ACCOUNTADMIN or MONITOR role for warehouse-level cost visibility' },
                          { num: 3, text: 'Copy your Account identifier (e.g. abc12345.us-east-1) from the URL' },
                          { num: 4, text: 'Paste Account, Username and Password below' },
                        ] : selectedProvider.id === 'databricks' ? [
                          { num: 1, text: 'Databricks workspace → User Settings → Developer tab' },
                          { num: 2, text: 'Under Access tokens → Generate new token (30-90 day expiry)' },
                          { num: 3, text: 'Copy the token (starts with "dapi...")' },
                          { num: 4, text: 'Paste workspace URL and token below' },
                        ] : selectedProvider.id === 'digitalocean' ? [
                          { num: 1, text: 'DO Cloud Panel → API → Tokens/Keys → Generate New Token' },
                          { num: 2, text: 'Name it "CARTIE Audit" → Scope: Read-only → Expiration: 90 days' },
                          { num: 3, text: 'Click "Generate Token" and copy (starts with "dop_v1_...")' },
                          { num: 4, text: 'Paste the token below' },
                        ] : [
                          { num: 1, text: `Go to ${selectedProvider.name} Dashboard → Settings or API section` },
                          { num: 2, text: 'Create a new API key or access token' },
                          { num: 3, text: 'Set permissions to "Read Only" if available' },
                          { num: 4, text: 'Copy the key/token and paste below' },
                        ]).map(s => (
                          <div key={s.num} className="flex items-start gap-2">
                            <div className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[11px] font-bold flex items-center justify-center shrink-0">{s.num}</div>
                            <p className="text-sm text-slate-700">{s.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="font-medium text-slate-800 mb-4">Paste your credentials</p>
                  <div className="space-y-3">
                    {/* Azure Tenant ID (required) */}
                    {selectedProvider.id === 'azure' && (
                      <div>
                        <label className="text-xs font-medium text-slate-400 mb-1 block">Tenant ID (Directory ID)</label>
                        <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                          className="font-mono text-sm" data-testid="tenant-id-input" />
                      </div>
                    )}

                    {/* Snowflake Account identifier */}
                    {selectedProvider.id === 'snowflake' && (
                      <div>
                        <label className="text-xs font-medium text-slate-400 mb-1 block">Account Identifier</label>
                        <Input placeholder="e.g. abc12345.us-east-1"
                          value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                          className="font-mono text-sm" data-testid="snowflake-account-input" />
                        <p className="text-[11px] text-slate-400 mt-1">Found in your Snowflake URL (left of snowflakecomputing.com).</p>
                      </div>
                    )}

                    {/* Databricks Workspace URL */}
                    {selectedProvider.id === 'databricks' && (
                      <div>
                        <label className="text-xs font-medium text-slate-400 mb-1 block">Workspace URL</label>
                        <Input placeholder="https://adb-1234.5.azuredatabricks.net"
                          value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                          className="font-mono text-sm" data-testid="databricks-workspace-input" />
                      </div>
                    )}

                    {/* GCP gets a file uploader instead of manual paste */}
                    {selectedProvider.id === 'gcp' ? (
                      <div>
                        <label className="text-xs font-medium text-slate-400 mb-1 block">Service Account JSON Key</label>
                        <input
                          id="gcp-json-input"
                          type="file"
                          accept=".json"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              setCredentials(ev.target.result);
                              toast.success(`${f.name} loaded`);
                            };
                            reader.readAsText(f);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById('gcp-json-input').click()}
                          className="w-full p-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/40 text-left transition-colors"
                          data-testid="gcp-json-upload-btn"
                        >
                          {credentials ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              <span className="text-sm text-slate-700 font-medium">JSON key loaded</span>
                              <span className="text-xs text-slate-400 ml-auto">click to replace</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-400">Upload your service account JSON key</span>
                            </div>
                          )}
                        </button>
                        <p className="text-[11px] text-slate-400 mt-1">
                          File is read in your browser only. We send the contents once to our server to run the scan, then discard it.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="text-xs font-medium text-slate-400 mb-1 block">
                            {selectedProvider.id === 'aws' ? 'Access Key ID' :
                             selectedProvider.id === 'azure' ? 'Client ID (Application ID)' :
                             selectedProvider.id === 'snowflake' ? 'Username' :
                             selectedProvider.id === 'databricks' ? 'Personal Access Token' :
                             selectedProvider.id === 'digitalocean' ? 'Personal Access Token' :
                             'API Key / Token'}
                          </label>
                          <Input placeholder={
                              selectedProvider.id === 'aws' ? 'AKIA...' :
                              selectedProvider.id === 'snowflake' ? 'Your Snowflake username' :
                              selectedProvider.id === 'databricks' ? 'dapi...' :
                              selectedProvider.id === 'digitalocean' ? 'dop_v1_...' :
                              'Your key or token'
                            }
                            value={credentials} onChange={(e) => setCredentials(e.target.value)}
                            className="font-mono text-sm" data-testid="credential-input" />
                        </div>
                        {(selectedProvider.id === 'aws' || selectedProvider.id === 'azure' || selectedProvider.id === 'snowflake') && (
                          <div>
                            <label className="text-xs font-medium text-slate-400 mb-1 block">
                              {selectedProvider.id === 'aws' ? 'Secret Access Key' :
                               selectedProvider.id === 'azure' ? 'Client Secret' :
                               'Password'}
                            </label>
                            <Input type="password" placeholder={
                                selectedProvider.id === 'aws' ? 'wJalr...' :
                                selectedProvider.id === 'snowflake' ? 'Your password' :
                                'Your secret'
                              }
                              value={secretKey} onChange={(e) => setSecretKey(e.target.value)}
                              className="font-mono text-sm" data-testid="secret-input" />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-blue-100">
                  <Shield className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Your credentials are safe</p>
                    <ul className="text-xs text-emerald-700 mt-1 space-y-0.5">
                      <li>&#x2022; Read-only access — CANNOT modify resources</li>
                      <li>&#x2022; Encrypted and deleted after scan</li>
                      <li>&#x2022; Scan completes in under 2 minutes</li>
                    </ul>
                  </div>
                </div>

                <Button className="bg-emerald-500 hover:bg-blue-600 w-full h-12 text-base" onClick={handleStartScan}
                  disabled={!shouldShowData && !credentials} data-testid="start-audit-btn">
                  <Zap className="w-5 h-5 mr-2" />
                  {shouldShowData ? 'Run Demo Audit' : credentials ? 'Scan My Account' : 'Enter credentials first'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Scanning Progress */}
          {scanning && (
            <Card>
              <CardContent className="p-12 text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-slate-700">Scanning your cloud account...</h3>
                <Progress value={scanProgress} className="max-w-md mx-auto" />
                <p className="text-sm text-slate-400">
                  {scanProgress < 30 ? 'Connecting to account...' : scanProgress < 60 ? 'Scanning resources and usage patterns...' : scanProgress < 90 ? 'Identifying waste and optimization opportunities...' : 'Generating your report...'}
                </p>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        /* Audit Results */
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-4"><p className="text-xs text-slate-400">Resources Scanned</p><p className="text-2xl font-bold text-slate-900">{auditResults.resourcesScanned}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-xs text-slate-400">Monthly Spend</p><p className="text-2xl font-bold text-slate-900">${auditResults.totalMonthlySpend.toLocaleString()}</p></CardContent></Card>
            <Card className="bg-red-50 border-red-200"><CardContent className="p-4"><p className="text-xs text-red-600">Waste Found</p><p className="text-2xl font-bold text-red-700">${auditResults.wasteFound.toLocaleString()}/mo</p><p className="text-xs text-red-500">{auditResults.wastePercent}% of spend</p></CardContent></Card>
            <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-4"><p className="text-xs text-emerald-600">Annual Savings</p><p className="text-2xl font-bold text-emerald-700">${(auditResults.wasteFound * 12).toLocaleString()}</p><p className="text-xs text-emerald-500">if optimized today</p></CardContent></Card>
          </div>

          {/* Savings Breakdown */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-500" />Savings Breakdown</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {auditResults.savingsBreakdown.map((cat, i) => (
                <div key={i} className="p-4 rounded-xl border border-slate-100 hover:border-emerald-200 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div><p className="font-semibold text-slate-800">{cat.category}</p><p className="text-xs text-slate-400">{cat.count} resources affected</p></div>
                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-sm">${cat.monthlySavings.toLocaleString()}/mo</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">{cat.items.map((item, j) => <span key={j} className="text-xs bg-slate-100 text-slate-400 px-2 py-1 rounded-full">{item}</span>)}</div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Quick Wins */}
          <Card className="border-emerald-200">
            <CardHeader><CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" />Top 3 Quick Wins</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {auditResults.quickWins.map((win, i) => (
                <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                  <div>
                    <p className="font-medium text-slate-800">{win.action}</p>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{win.effort}</span>
                      <span className="text-xs text-slate-400">Risk: {win.risk}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-emerald-600">${win.savings.toLocaleString()}/mo</p>
                    <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-7 text-xs mt-1" onClick={() => toast.success('Fix applied!')}>
                      <Zap className="w-3 h-3 mr-1" />Apply Fix
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="text-center">
            <Button onClick={() => { setAuditResults(null); setSelectedProvider(null); setCurrentStep(0); setCredentials(''); }} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />Run Another Audit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudAudit;
